import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Plus, Pencil, Trash2, AlertCircle } from 'lucide-react';
import { useHolidays } from '../hooks/trHolidays';

const TYPE_STYLES = {
  public_holiday: 'bg-amber-100 text-amber-700',
  school_vacation: 'bg-blue-100 text-blue-700',
  exceptional: 'bg-red-100 text-red-700',
};

function HolidayDialog({ open, editTarget, form, holidayTypes, submitting, onClose, onChange, onSubmit }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {editTarget ? 'Modifier la période' : 'Nouvelle période de vacances'}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Intitulé</label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => onChange('label', e.target.value)}
              placeholder="Ex: Vacances de Noël"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={form.type}
              onChange={(e) => onChange('type', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {holidayTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => onChange('start_date', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
              <input
                type="date"
                value={form.end_date}
                min={form.start_date}
                onChange={(e) => onChange('end_date', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {submitting ? 'Enregistrement...' : editTarget ? 'Modifier' : 'Créer'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function HolidayRow({ holiday, onEdit, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const isActive = holiday.start_date <= today && today <= holiday.end_date;
  const isPast = holiday.end_date < today;

  const formatDate = (d) =>
    new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });

  const typeBadgeClass = TYPE_STYLES[holiday.type] ?? 'bg-gray-100 text-gray-600';
  const typeLabels = {
    public_holiday: 'Jour férié',
    school_vacation: 'Vacances',
    exceptional: 'Exceptionnel',
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50">
      <div className="flex items-start gap-3 min-w-0">
        <div
          className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${isActive ? 'bg-amber-400' : isPast ? 'bg-gray-300' : 'bg-blue-400'}`}
        />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="text-sm font-medium text-gray-900 truncate">{holiday.label}</p>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeBadgeClass}`}>
              {typeLabels[holiday.type] ?? holiday.type}
            </span>
            {isActive && (
              <span className="text-xs font-medium px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">En cours</span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {formatDate(holiday.start_date)} → {formatDate(holiday.end_date)}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0 ml-2">
        {!isPast && (
          <button
            onClick={() => onEdit(holiday)}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
        )}
        {confirmDelete ? (
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                onDelete(holiday.id);
                setConfirmDelete(false);
              }}
              className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              Confirmer
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Annuler
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function Holidays({ embedded = false }) {
  const {
    holidays,
    loading,
    submitting,
    dialogOpen,
    editTarget,
    form,
    HOLIDAY_TYPES,
    openCreate,
    openEdit,
    closeDialog,
    handleFormChange,
    handleSubmit,
    handleDelete,
    isCurrentlyOnHoliday,
  } = useHolidays();

  const today = new Date().toISOString().split('T')[0];
  const upcoming = holidays.filter((h) => h.end_date >= today);
  const past = holidays.filter((h) => h.end_date < today);

  return (
    <div className={embedded ? '' : 'p-6 max-w-3xl mx-auto'}>
      <HolidayDialog
        open={dialogOpen}
        editTarget={editTarget}
        form={form}
        holidayTypes={HOLIDAY_TYPES}
        submitting={submitting}
        onClose={closeDialog}
        onChange={handleFormChange}
        onSubmit={handleSubmit}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-xl">
            <Calendar className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Vacances scolaires</h1>
            <p className="text-sm text-gray-500">Aucun trajet ni notification pendant ces périodes</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          Ajouter une période
        </button>
      </div>

      {/* Active holiday banner */}
      {isCurrentlyOnHoliday() && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 p-4 mb-4 bg-amber-50 border border-amber-200 rounded-xl"
        >
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Vacances en cours</p>
            <p className="text-xs text-amber-700">
              Les trajets ne démarrent pas automatiquement et les parents ne reçoivent aucune notification.
            </p>
          </div>
        </motion.div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2" />
          <p className="text-sm text-gray-500">Chargement...</p>
        </div>
      ) : holidays.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Aucune période de vacances définie</p>
          <button onClick={openCreate} className="mt-3 text-blue-600 text-sm hover:underline">
            Ajouter la première période
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {upcoming.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  À venir &amp; en cours ({upcoming.length})
                </h3>
              </div>
              {upcoming.map((h) => (
                <HolidayRow key={h.id} holiday={h} onEdit={openEdit} onDelete={handleDelete} />
              ))}
            </div>
          )}

          {past.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden opacity-75">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Passées ({past.length})</h3>
              </div>
              {past.map((h) => (
                <HolidayRow key={h.id} holiday={h} onEdit={openEdit} onDelete={handleDelete} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
